import styled, { css } from 'styled-components';

import { breakpoints } from 'theme';
import {
  SCHEDULER_TAB_BLOCK_GAP,
  SCHEDULER_TAB_BLOCK_MARGIN_BOTTOM,
  SCHEDULER_TAB_BLOCK_PADDING_LEFT,
  SCHEDULER_TAB_BLOCK_PADDING_MOBILE,
  SCHEDULER_TAB_BLOCK_PADDING_RIGHT,
  SCHEDULER_TAB_LIST_MIN_HEIGHT,
  SCHEDULER_TAB_LIST_PADDING,
  SCHEDULER_TAB_LIST_PADDING_MOBILE,
  SIDEBAR_SPACE_4,
  SIDEBAR_SPACE_8,
  SIDEBAR_SPACE_24,
} from 'styles/sidebarMenuListItemHighlight';

import { TabsNavigationLayout, TabsVariant } from './types';

const isNavigationVariant = (variant: TabsVariant) => variant === 'navigation';

/** Flex slot for SearchInput: grows/shrinks with viewport; full row on smaller laptops */
export const StyledTabsSearchSlot = styled.div`
  flex: 1 1 220px;
  min-width: 0;
  max-width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media ${breakpoints.downMdPlus} {
    flex: 1 1 100%;
    justify-content: stretch;
    width: 100%;
  }
`;

export const StyledTabsBlock = styled.div<{
  variant: TabsVariant;
  navigationLayout?: TabsNavigationLayout;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SCHEDULER_TAB_BLOCK_GAP};
  padding-left: ${({ variant, navigationLayout }) => {
    if (variant === 'sidebar') {
      return '17px';
    }
    if (isNavigationVariant(variant)) {
      return navigationLayout === 'panel'
        ? SIDEBAR_SPACE_24
        : SCHEDULER_TAB_BLOCK_PADDING_LEFT;
    }
    return SIDEBAR_SPACE_24;
  }};
  padding-right: ${({ variant, navigationLayout }) => {
    if (variant === 'sidebar') {
      return '5px';
    }
    if (isNavigationVariant(variant)) {
      return navigationLayout === 'panel'
        ? SIDEBAR_SPACE_24
        : SCHEDULER_TAB_BLOCK_PADDING_RIGHT;
    }
    return SCHEDULER_TAB_BLOCK_PADDING_RIGHT;
  }};
  min-height: ${SCHEDULER_TAB_LIST_MIN_HEIGHT};
  overflow-y: hidden;
  overflow-x: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  flex-wrap: wrap;
  margin: ${({ variant }) => {
    if (variant === 'sidebar') {
      return '5px 0';
    }
    return `0 0 ${SCHEDULER_TAB_BLOCK_MARGIN_BOTTOM} 0`;
  }};

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${breakpoints.downMdPlus} {
    align-items: stretch;
  }

  @media ${breakpoints.downSmPlus} {
    flex-direction: column;
    align-items: stretch;
    padding-left: ${({ variant }) =>
      variant === 'sidebar' ? '17px' : SCHEDULER_TAB_BLOCK_PADDING_MOBILE};
    padding-right: ${({ variant }) =>
      variant === 'sidebar' ? '5px' : SCHEDULER_TAB_BLOCK_PADDING_MOBILE};
    min-height: unset;
    height: auto;

    ${({ variant }) =>
      variant === 'legacy' &&
      css`
        min-height: 32px;
      `}

    &:after {
      display: none;
    }
  }

  &:after {
    content: '';
    position: absolute;
    right: 0;
    width: 25px;
    height: 100%;
    max-height: ${SCHEDULER_TAB_LIST_MIN_HEIGHT};
    background: linear-gradient(90deg, transparent, white 40%);
    z-index: 4;
    pointer-events: none;

    @media ${breakpoints.downSmPlus} {
      display: none;
    }
  }
`;

export const StyledTabsList = styled.ol<{
  variant: TabsVariant;
  width?: string;
}>`
  border-radius: 16px;
  padding: ${({ variant }) => {
    if (variant === 'sidebar') {
      return `0 ${SIDEBAR_SPACE_4}`;
    }
    if (isNavigationVariant(variant)) {
      return SCHEDULER_TAB_LIST_PADDING;
    }
    return `0 ${SIDEBAR_SPACE_8}`;
  }};
  gap: 0;
  background-color: ${({ theme }) => theme.color.blackSqueeze};
  display: flex;
  list-style-type: none;
  height: ${({ variant }) =>
    isNavigationVariant(variant) ? 'auto' : SCHEDULER_TAB_LIST_MIN_HEIGHT};
  min-height: ${({ variant }) =>
    isNavigationVariant(variant) ? 'auto' : SCHEDULER_TAB_LIST_MIN_HEIGHT};
  width: ${({ width }) => width ?? 'auto'};
  margin: 0;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;

  @media ${breakpoints.downMdPlus} {
    flex: 1 1 auto;
    max-width: 100%;
    width: 100%;
  }

  @media ${breakpoints.downSmPlus} {
    width: 100%;
    height: auto;
    min-height: auto;
    padding: ${({ variant }) =>
      isNavigationVariant(variant) || variant === 'legacy'
        ? SCHEDULER_TAB_LIST_PADDING_MOBILE
        : `0 ${SIDEBAR_SPACE_4}`};
    scroll-padding-inline: ${SCHEDULER_TAB_BLOCK_PADDING_MOBILE};
  }
`;
